import React from 'react';
import PropTypes from 'prop-types';

import {Box, ButtonBase, CircularProgress, Popper, Stack, TextField, Typography} from 'views/components';
import {InputAdornment} from '@mui/material';

import backend from 'utils/backend';

import {Translate, translate} from '../Translate';

const SlashCommandTextbox = (props) => {
  const {isExecutingCommand, onExecutePlaybook, onExecuteSkill, placeholder, playbooks, onInputValueChange} = props;

  const [value, setValue] = React.useState('');
  const [availableSkills, setAvailableSkills] = React.useState([]);
  const [isLoadingCommands, setIsLoadingCommands] = React.useState(false);
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);

  const anchorRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const availableCommands = React.useMemo(() => {
    const skillCommands = availableSkills.map((skill) => ({
      key: skill.skillRn,
      name: skill.name,
      type: 'skill',
      item: skill,
    }));
    const playbookCommands = playbooks.map((playbook) => ({
      key: playbook.playbookRn,
      name: playbook.title,
      type: 'playbook',
      item: playbook,
    }));

    return [...skillCommands, ...playbookCommands];
  }, [availableSkills, playbooks]);

  const command = React.useMemo(() => {
    if (!value.startsWith('/')) {
      return {
        isSlashCommand: false,
        commandName: '',
        isComplete: false,
        promptText: '',
      };
    }

    const matchedCommand = [...availableCommands]
        .filter((commandOption) => {
          const commandPrefix = `/${commandOption.name}`;
          return value === commandPrefix || value.startsWith(`${commandPrefix} `);
        })
        .sort((left, right) => (right?.name || '').length - (left?.name || '').length)[0] || null;

    if (matchedCommand?.name) {
      const matchedCommandPrefix = `/${matchedCommand.name}`;
      const isExactMatch = value === matchedCommandPrefix;
      const hasPromptText = value.length > matchedCommandPrefix.length;

      return {
        isSlashCommand: true,
        commandName: matchedCommand.name,
        isComplete: matchedCommand.type === 'playbook' ? isExactMatch || hasPromptText : hasPromptText,
        promptText: hasPromptText ? value.slice(matchedCommandPrefix.length + 1) : '',
      };
    }

    const firstSpaceIndex = value.indexOf(' ');

    return {
      isSlashCommand: true,
      commandName: value.slice(1, firstSpaceIndex === -1 ? value.length : firstSpaceIndex),
      isComplete: firstSpaceIndex !== -1,
      promptText: firstSpaceIndex === -1 ? '' : value.slice(firstSpaceIndex + 1),
    };
  }, [availableCommands, value]);

  const filteredCommands = command.isSlashCommand && !command.isComplete ?
    availableCommands.filter((commandOption) => {
      if (!command.commandName) {
        return true;
      }

      const escapedCommandName = command.commandName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return new RegExp(`^${escapedCommandName}`, 'i').test(commandOption?.name || '');
    }) :
    [];

  const selectedCommand = availableCommands.find(
      (commandOption) => (commandOption?.name || '').toLowerCase() === command.commandName.toLowerCase(),
  ) || null;
  const showMenu = isMenuOpen && command.isSlashCommand && !command.isComplete;
  const menuWidth = anchorRef.current ? `${anchorRef.current.offsetWidth}px` : '100%';

  const focusInput = () => {
    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleChange = (nextValue) => {
    setValue(nextValue);
    onInputValueChange(nextValue);
  };

  const selectCommand = (commandOption) => {
    if (!commandOption?.name) {
      return;
    }

    setIsMenuOpen(false);
    setValue(`/${commandOption.name} `);
    focusInput();
  };

  const executeCommand = async () => {
    if (isExecutingCommand || !selectedCommand || !command.isComplete) {
      return;
    }

    const response = selectedCommand.type === 'playbook' ?
      await onExecutePlaybook(selectedCommand.item) :
      await onExecuteSkill(selectedCommand.item, command.promptText);

    if (response !== null) {
      setValue('');
      setIsMenuOpen(false);
    }
  };

  const handleKeyDown = (event) => {
    if (showMenu) {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsMenuOpen(false);
        return;
      }

      if (isLoadingCommands || filteredCommands.length === 0) {
        return;
      }

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % filteredCommands.length);
        return;
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
        return;
      }

      if (event.key === 'Enter' || event.key === 'Tab') {
        const nextCommand = filteredCommands[highlightedIndex] || filteredCommands[0];

        if (nextCommand) {
          event.preventDefault();
          selectCommand(nextCommand);
        }
        return;
      }
    }

    if (event.key === 'Enter' && command.isSlashCommand) {
      event.preventDefault();
      executeCommand();
    }
  };

  React.useEffect(() => {
    let isMounted = true;

    const loadSkills = async () => {
      setIsLoadingCommands(true);

      try {
        const response = await backend.skills.getSkills();

        if (!isMounted) {
          return;
        }

        setAvailableSkills(
            [...response.list]
                .sort((left, right) => (left?.name || '').localeCompare(right?.name || '')),
        );
      } catch {
        if (!isMounted) {
          return;
        }

        setAvailableSkills([]);
      } finally {
        if (isMounted) {
          setIsLoadingCommands(false);
        }
      }
    };

    loadSkills();

    return () => {
      isMounted = false;
    };
  }, []);

  React.useEffect(() => {
    if (!command.isSlashCommand || command.isComplete) {
      setIsMenuOpen(false);
      return;
    }

    setIsMenuOpen(true);
    setHighlightedIndex(0);
  }, [command.commandName, command.isComplete, command.isSlashCommand]);

  return (
    <Box
      sx={{
        p: '12px',
      }}
    >
      <Box ref={anchorRef}>
        <TextField
          value={value}
          onChange={handleChange}
          fullWidth
          placeholder={placeholder || translate('skills.SkillsModule.Textbox.Placeholder')}
          autoComplete='off'
          disabled={isExecutingCommand}
          inputRef={inputRef}
          InputProps={{
            endAdornment: isExecutingCommand ? (
              <InputAdornment position='end'>
                <CircularProgress color='inherit' size={20} />
              </InputAdornment>
            ) : null,
          }}
          onFocus={() => {
            if (command.isSlashCommand && !command.isComplete) {
              setIsMenuOpen(true);
            }
          }}
          onKeyDown={handleKeyDown}
          sx={{
            '& .MuiOutlinedInput-root': {
              'backgroundColor': 'common.white',
              'borderRadius': '4px',
              '& fieldset': {
                'borderStyle': 'dashed',
                'borderColor': '#A8A49E',
              },
            },
            '& .MuiOutlinedInput-input': {
              'px': '16px',
              'py': '10px',
              'fontSize': '11px',
              'lineHeight': '150%',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              'color': '#A8A49E',
              'opacity': 1,
            },
          }}
        />
      </Box>
      <Popper
        open={showMenu}
        anchorEl={anchorRef.current}
        onClickAway={() => setIsMenuOpen(false)}
        placement='bottom-start'
        zIndex={1400}
        GrowSx={{
          width: menuWidth,
        }}
      >
        <Stack
          sx={{
            width: menuWidth,
            maxHeight: '280px',
            overflowY: 'auto',
            py: '6px',
          }}
        >
          {
            isLoadingCommands &&
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='center'
              sx={{
                px: '16px',
                py: '18px',
                gap: '10px',
              }}
            >
              <CircularProgress size={18} />
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#6B6760',
                  lineHeight: '150%',
                }}
              >
                <Translate expression='skills.SkillsModule.Textbox.Loading' />
              </Typography>
            </Stack>
          }
          {
            !isLoadingCommands && filteredCommands.length === 0 &&
            <Typography
              sx={{
                px: '16px',
                py: '12px',
                fontSize: '12px',
                fontWeight: 400,
                color: '#A8A49E',
                lineHeight: '150%',
              }}
            >
              <Translate expression='skills.SkillsModule.Textbox.NoMatch' />
            </Typography>
          }
          {
            !isLoadingCommands && filteredCommands.map((commandOption, index) => {
              const isHighlighted = index === highlightedIndex;

              return (
                <ButtonBase
                  key={commandOption.key}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => selectCommand(commandOption)}
                  sx={{
                    'width': '100%',
                    'justifyContent': 'flex-start',
                    'textAlign': 'left',
                    'px': '14px',
                    'py': '10px',
                    'backgroundColor': isHighlighted ? '#F8F6F3' : '#FFFFFF',
                    '&:hover': {
                      backgroundColor: '#F8F6F3',
                    },
                  }}
                >
                  <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    sx={{
                      width: '100%',
                      gap: '16px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '12px',
                        fontWeight: 400,
                        color: '#1A1916',
                        lineHeight: '100%',
                      }}
                    >
                      /{commandOption.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '10px',
                        fontWeight: 500,
                        color: '#A8A49E',
                        lineHeight: '100%',
                        textTransform: 'uppercase',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {commandOption.type}
                    </Typography>
                  </Stack>
                </ButtonBase>
              );
            })
          }
        </Stack>
      </Popper>
    </Box>
  );
};

SlashCommandTextbox.propTypes = {
  isExecutingCommand: PropTypes.bool.isRequired,
  onExecutePlaybook: PropTypes.func,
  onExecuteSkill: PropTypes.func.isRequired,
  onInputValueChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  playbooks: PropTypes.array,
};

export default React.memo(SlashCommandTextbox);
